'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useSiteLanguage } from './SiteLanguageProvider';
import type { SiteLanguage } from '@/lib/site-language-config';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const pathname = usePathname();
  const { language, setLanguage } = useSiteLanguage();

  const copy = {
    en: {
      skipToContent: 'Skip to main content',
      navigationLabel: 'Main navigation',
      more: 'More',
      startProject: 'Start Your Project',
      openMobileMenu: 'Open mobile menu',
      closeMobileMenu: 'Close mobile menu',
      mobileNavigation: 'Mobile navigation',
      languageSwitcher: 'Language selection',
    },
    fr: {
      skipToContent: 'Passer au contenu principal',
      navigationLabel: 'Navigation principale',
      more: 'Plus',
      startProject: 'Commencer votre projet',
      openMobileMenu: 'Ouvrir le menu mobile',
      closeMobileMenu: 'Fermer le menu mobile',
      mobileNavigation: 'Navigation mobile',
      languageSwitcher: 'Sélection de la langue',
    },
  } satisfies Record<SiteLanguage, Record<string, string>>;

  const t = copy[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when pathname changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  /** Direct URLs still work; these must not appear in the marketing menu (esp. mobile drawer). */
  const excludedFromMarketingMenu = new Set(['/marketplace', '/fidelity', '/dating-site']);

  const navigationItems = {
    en: [
      { label: 'Home', href: '/home', icon: 'HomeIcon' },
      { label: 'Services', href: '/services', icon: 'CubeIcon' },
      { label: 'About', href: '/about', icon: 'UserGroupIcon' },
    ],
    fr: [
      { label: 'Accueil', href: '/home', icon: 'HomeIcon' },
      { label: 'Services', href: '/services', icon: 'CubeIcon' },
      { label: 'À propos', href: '/about', icon: 'UserGroupIcon' },
    ],
  } satisfies Record<SiteLanguage, { label: string; href: string; icon: string }[]>;

  const moreItems = {
    en: [
      { label: 'Contact', href: '/contact', icon: 'EnvelopeIcon' },
      { label: 'Careers', href: '/careers', icon: 'AcademicCapIcon' },
      { label: 'Training', href: '/training', icon: 'AcademicCapIcon' },
    ],
    fr: [
      { label: 'Contact', href: '/contact', icon: 'EnvelopeIcon' },
      { label: 'Carrières', href: '/careers', icon: 'AcademicCapIcon' },
      { label: 'Formation', href: '/training', icon: 'AcademicCapIcon' },
    ],
  } satisfies Record<SiteLanguage, { label: string; href: string; icon: string }[]>;

  const renderedNavigationItems = navigationItems[language].filter(
    (item) => !excludedFromMarketingMenu.has(item.href)
  );
  const renderedMoreItems = moreItems[language].filter(
    (item) => !excludedFromMarketingMenu.has(item.href)
  );

  const mobileNavItems = [...renderedNavigationItems, ...renderedMoreItems];

  const isActive = (href: string) => pathname === href;

  const LanguageToggle = ({ mobile = false }: { mobile?: boolean }) => (
    <div
      className={`inline-flex items-center rounded-full border border-border bg-card/80 p-1 ${
        mobile ? 'w-full justify-center' : ''
      }`}
      aria-label={t.languageSwitcher}
      role="group"
    >
      <button
        type="button"
        onClick={() => setLanguage('fr')}
        className={`${mobile ? 'min-h-[44px] flex-1 ' : ''}rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] transition-colors duration-200 ${
          language === 'fr'
            ? 'bg-foreground text-background'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        aria-pressed={language === 'fr'}
      >
        FR
      </button>
      <button
        type="button"
        onClick={() => setLanguage('en')}
        className={`${mobile ? 'min-h-[44px] flex-1 ' : ''}rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] transition-colors duration-200 ${
          language === 'en'
            ? 'bg-foreground text-background'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        aria-pressed={language === 'en'}
      >
        EN
      </button>
    </div>
  );

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-6 focus:py-3 focus:bg-primary focus:text-primary-foreground focus:rounded-sm focus:shadow-neon-cyan-intense focus:outline-none focus:ring-2 focus:ring-primary/50"
      >
        {t.skipToContent}
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isMobileMenuOpen
            ? 'bg-background border-b border-border/70'
            : isScrolled
              ? 'navbar-glass scrolled'
              : 'bg-transparent'
        } ${className}`}
      >
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link href="/home" className="flex items-center group">
              <span className="font-display text-card-title text-gradient-neon leading-none">
                Neon Fyber
              </span>
            </Link>

            <nav
              className="hidden lg:flex items-center space-x-1"
              role="navigation"
              aria-label={t.navigationLabel}
            >
              {renderedNavigationItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`relative px-4 py-2 transition-colors duration-300 group focus:outline-none rounded-sm ${
                      active ? 'text-primary' : 'text-foreground hover:text-primary'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="relative z-10 text-nav font-medium">{item.label}</span>
                    <span
                      className={`absolute bottom-0 left-0 h-0.5 bg-primary transition-all duration-300 ease-out ${
                        active
                          ? 'w-full shadow-neon-cyan'
                          : 'w-0 group-hover:w-full group-focus:w-full'
                      }`}
                    />
                  </Link>
                );
              })}

              <div className="relative">
                <button
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                  className="flex items-center space-x-1 px-4 py-2 text-foreground hover:text-primary transition-colors duration-300 focus:outline-none focus:text-primary rounded-sm"
                  aria-expanded={isMoreMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="text-nav font-medium">{t.more}</span>
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={`transition-transform duration-300 ${
                      isMoreMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isMoreMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-neon-cyan overflow-hidden animate-fade-in">
                    {renderedMoreItems.map((item) => {
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={`flex items-center space-x-3 px-4 py-3 transition-colors duration-200 focus:outline-none ${
                            active
                              ? 'bg-muted text-primary'
                              : 'text-foreground hover:bg-muted hover:text-primary focus:bg-muted focus:text-primary'
                          }`}
                          onClick={() => setIsMoreMenuOpen(false)}
                          role="menuitem"
                          aria-current={active ? 'page' : undefined}
                        >
                          <Icon name={item.icon} size={20} />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <LanguageToggle />
              <Link
                href="/contact"
                className="px-6 py-2.5 bg-primary text-primary-foreground text-nav font-medium rounded-sm hover:shadow-neon-cyan-intense transition-all duration-300 hover:scale-105 focus:outline-none focus:shadow-neon-cyan-intense focus:scale-105"
              >
                {t.startProject}
              </Link>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-300 focus:outline-none focus:text-primary rounded-sm"
              aria-label={isMobileMenuOpen ? t.closeMobileMenu : t.openMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-background/60 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className={`fixed top-20 right-0 h-[calc(100dvh-5rem)] max-h-[calc(100dvh-5rem)] w-full sm:w-80 bg-card border-l border-border backdrop-blur-glass z-40 overflow-hidden lg:hidden transform transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label={t.mobileNavigation}
        >
          <nav className="h-full overflow-y-auto overscroll-contain px-6 py-4 space-y-1 pb-8">
            <div className="pb-4">
              <LanguageToggle mobile />
            </div>
            {mobileNavItems.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors duration-200 focus:outline-none ${
                    active
                      ? 'bg-muted text-primary border-l-2 border-primary'
                      : 'text-foreground hover:bg-muted hover:text-primary focus:bg-muted focus:text-primary'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="text-nav font-medium">{item.label}</span>
                </Link>
              );
            })}
            <div className="pt-4">
              <Link
                href="/contact"
                className="block w-full px-6 py-3 bg-primary text-primary-foreground text-nav font-medium rounded-sm text-center hover:shadow-neon-cyan-intense transition-all duration-300 focus:outline-none focus:shadow-neon-cyan-intense focus:scale-105"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.startProject}
              </Link>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
